/**
 * 이상한 방 - 카탈로그 배치 힌트 읽기 (놓을 수 있는 소품의 판정, 한 벌)
 *
 * 근거 문서: `이상한방-상태모델-v0.1.md` 8절(카탈로그 규격) · `entryTypes.ts`의 크기 등급(`SizeClass`)
 *
 * ## 왜 이 파일이 카탈로그 층에 있는가
 *
 * "생김에 쓸 수 있는 소품 = 바닥에 놓이는 등급(`anchor` · `floor` · `surface`), 힌트가 없으면 허용"이라는 판정을
 * 생성기(`game/changeGen.addCandidates`)와 사람 체인저의 도구(`input/changerLocks.pickChangerCatalog`)가 함께 쓴다.
 * `input/`은 `game/`을 보지 않으므로(CLAUDE.md 레이어 방향) 둘이 함께 볼 수 있는 층은 여기뿐이다 - 한때 같은 값을 두 파일이
 * 각자 적고 있었고, 그건 같은 코드가 아니라 같은 값을 두 번 적은 것이다. 벽면물·천장물이 늘거나 등급이 하나 생기는 날
 * 한쪽만 갱신되면 생성기가 놓는 것과 사람이 놓을 수 있는 것이 갈린다.
 *
 * ## 구조적으로 읽는다
 *
 * `CatalogEntry`(코어 규격)에는 `sizeClass`가 없다 - 판정에 안 쓰는 필드는 판정 타입에 들어가지 않는다(`catalog.ts` 머리말).
 * 그래서 힌트는 구조적으로 읽고, 힌트가 없는 카탈로그(테스트 픽스처)로도 굴러간다. 캐스트는 이 파일에만 있다.
 */

import type { CatalogEntry } from '../core/types'
import { SIZE_CLASSES, type SizeClass } from './entryTypes'

/** 카탈로그 레이어가 코어 규격 위에 얹는 힌트. 여기서만 캐스트한다. */
type SizeHinted = CatalogEntry & Readonly<{ sizeClass?: unknown }>

const SIZE_CLASS_SET: ReadonlySet<string> = new Set(SIZE_CLASSES)

/**
 * 바닥(가구 상판 포함)에 놓이는 등급. 벽면물·천장물을 바닥에 눕히면 변화가 아니라 렌더 사고로 보인다.
 * `SizeClass`를 전부 나열하지 않는 이유는 새 등급이 생기면 "놓을 수 있는가"를 여기서 한 번 결정하게 하려는 것이다.
 */
const FLOOR_PLACEABLE: ReadonlySet<SizeClass> = new Set<SizeClass>(['anchor', 'floor', 'surface'])

/** 항목의 크기 등급. 힌트가 없거나 규격 밖의 값이면 `undefined`. */
export function sizeClassOf(entry: CatalogEntry): SizeClass | undefined {
  const value = (entry as SizeHinted).sizeClass
  return typeof value === 'string' && SIZE_CLASS_SET.has(value) ? (value as SizeClass) : undefined
}

/** 생김(`add`)에 쓸 수 있는 소품인가. 힌트가 없는 항목은 제약을 선언한 적이 없으므로 허용한다. */
export function isFloorPlaceable(entry: CatalogEntry): boolean {
  const sizeClass = sizeClassOf(entry)
  return sizeClass === undefined || FLOOR_PLACEABLE.has(sizeClass)
}
